// Gift card products are created by hand in the admin, so their titles and
// SKUs are inconsistent ("1 Year - Denver Membership", "1 Month - Casual
// Plan", "3 Month - Avid Plan Membership", ...). These helpers read a title
// loosely and sort each gift card into the groups shown on the gift cards
// page. Anything that can't be recognized falls back to "Other Amounts" so a
// gift card never disappears from the page.

// plan and pack details use the same wording as Ways to Rent
export const plans = {
  casual: {
    name: 'Casual Puzzler Plan',
    steps: ['3 puzzles per month', '1 shipment per month'],
  },
  avid: {
    name: 'Avid Puzzler Plan',
    steps: ['6 puzzles per month', '2 shipments per month'],
    popular: true,
  },
  denver: {
    name: 'Denver Puzzler Plan',
    steps: ['Unlimited puzzles per month', 'Puzzles picked up in store'],
    // shown in bold so out-of-state buyers don't pick it by mistake
    highlight: 'Puzzles picked up in store',
  },
}
// shown in the order they're listed above
const planOrder = Object.keys(plans)

export const packs = {
  pack: {
    name: 'Puzzle Pack',
    steps: [
      'Rent up to 4 mid-size puzzles',
      'Or up to 3 large puzzles (1,500 pieces or more)',
      '60 days to complete your puzzles',
    ],
  },
  mini: {
    name: 'Mini Puzzle Pack',
    steps: [
      'Rent up to 2 mid-size puzzles',
      'Or 1 large puzzle (1,500 pieces or more)',
      '60 days to complete your puzzles',
    ],
  },
}
const packOrder = Object.keys(packs)

// the order the owner wants membership lengths shown in
const durationOrder = [3, 1, 12]

const numberWords = {
  one: 1,
  two: 2,
  three: 3,
  four: 4,
  five: 5,
  six: 6,
  seven: 7,
  eight: 8,
  nine: 9,
  ten: 10,
  eleven: 11,
  twelve: 12,
}
const monthsPattern = new RegExp(
  `\\b(\\d+|${Object.keys(numberWords).join('|')})\\s*(year|yr|month|mo)s?\\b`,
)

// words a Puzzle Pack title may have; anything else means it's some other
// kind of pack we don't have details for
const packWords = ['puzzle', 'puzzles', 'pack', 'packs', 'mini', 'gift', 'card', 'giftcard']

function normalize(title) {
  return (title || '')
    .toLowerCase()
    .replace(/[^a-z0-9$]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

function findPlan(text) {
  return planOrder.find((p) => text.indexOf(p) > -1) || null
}

// returns the membership length in months, or null if the title has none
function findMonths(text) {
  if (/\b(annual|annually|yearly)\b/.test(text)) return 12

  let m = text.match(monthsPattern)
  if (m) {
    let n = numberWords[m[1]] || parseInt(m[1], 10)
    return m[2] === 'year' || m[2] === 'yr' ? n * 12 : n
  }

  if (/\bmonthly\b/.test(text)) return 1
  if (/\bquarter(ly)?\b/.test(text)) return 3
  return null
}

// "$50 Gift Card", "50", "$50.00 giftcard" ... a card that's just an amount
export function isPlainAmount(title) {
  return /^\$?\s*\d+(\.\d+)?\s*(gift\s*card)?$/i.test((title || '').trim())
}

export function durationLabel(months) {
  if (months === 12) return '1 Year'
  if (months % 12 === 0) return `${months / 12} Years`
  return months === 1 ? '1 Month' : `${months} Months`
}

// describe a single gift card product
export function classify(product) {
  let text = normalize(product.title)

  if (/\bpacks?\b/.test(text)) {
    let known = text.split(' ').every((w) => packWords.includes(w) || /^\$?\d+$/.test(w))
    if (!known) return { kind: 'other' }
    return { kind: 'pack', key: /\bmini\b/.test(text) ? 'mini' : 'pack' }
  }

  let plan = findPlan(text)
  let months = plan ? findMonths(text) : null
  if (plan && months) {
    return { kind: 'membership', plan, months }
  }

  return { kind: 'other' }
}

function byOrder(order, key) {
  return (a, b) => {
    let ia = order.indexOf(key(a))
    let ib = order.indexOf(key(b))
    if (ia === -1) ia = order.length + key(a)
    if (ib === -1) ib = order.length + key(b)
    return ia - ib
  }
}

// group gift card products for display
export function groupGiftCards(products) {
  let memberships = {}
  let packCards = {}
  let other = []

  for (let product of products || []) {
    let c = classify(product)

    if (c.kind === 'membership') {
      memberships[c.months] = memberships[c.months] || {}
      // if the admin made duplicates, keep the first one listed
      if (!memberships[c.months][c.plan]) {
        memberships[c.months][c.plan] = { ...plans[c.plan], key: c.plan, product }
      } else {
        other.push({ product })
      }
    } else if (c.kind === 'pack') {
      if (!packCards[c.key]) {
        packCards[c.key] = { ...packs[c.key], key: c.key, product }
      } else {
        other.push({ product })
      }
    } else {
      other.push({ product })
    }
  }

  // the admin sometimes makes the same plain amount twice; show it once
  let seenAmounts = {}
  other = other.filter(({ product }) => {
    if (!isPlainAmount(product.title)) return true
    if (seenAmounts[product.price]) return false
    seenAmounts[product.price] = true
    return true
  })

  let durations = Object.keys(memberships)
    .map((m) => parseInt(m, 10))
    .sort(byOrder(durationOrder, (m) => m))
    .map((months) => ({
      months,
      label: durationLabel(months),
      cards: planOrder
        .filter((p) => memberships[months][p])
        .map((p) => memberships[months][p]),
    }))

  // the same memberships, one entry per plan with its lengths as options
  let byPlan = planOrder
    .map((p) => ({
      ...plans[p],
      key: p,
      options: durations
        .filter((d) => d.cards.some((c) => c.key === p))
        .map((d) => ({
          months: d.months,
          label: d.label,
          product: d.cards.find((c) => c.key === p).product,
        })),
    }))
    .filter((p) => p.options.length)

  return {
    durations,
    plans: byPlan,
    packs: packOrder.filter((k) => packCards[k]).map((k) => packCards[k]),
    // largest amount first
    other: other.sort((a, b) => b.product.price - a.product.price),
  }
}
